/**
 * Keeping the author's place across the studio's switch into and out of edit
 * mode (ADR-0051). The two views lay the same words out differently — the
 * reading view as sentences or lines, the editor as a box per paragraph — so
 * the place is carried as the words at the top of the screen and where they
 * sat, not as a scroll offset that means something else on the other side.
 */

/** Below the sticky parts of the page, where "the top of the screen" begins. */
const TOP_EDGE = 72;

export interface ViewAnchor {
  text: string;
  /** Where those words sat, from the top of the viewport. */
  top: number;
  /**
   * How many boxes or lines above it say the same thing, so a sentence that
   * recurs is found where it was and not where it first appears.
   */
  nth: number;
  /** How far down the page, as a fraction, for when the words are not found. */
  fraction: number;
}

const squash = (s: string) => s.replace(/\s+/g, " ").trim();

function firstBelowEdge<T extends HTMLElement>(
  selector: string,
  text: (el: T) => string,
): { el: T; text: string; nth: number } | null {
  const seen = new Map<string, number>();
  for (const el of document.querySelectorAll<T>(selector)) {
    const said = squash(text(el));
    // An inline sentence that wraps has a box per line; its last says whether
    // any of it is still below the edge.
    const rects = el.getClientRects();
    const last = rects[rects.length - 1];
    if (last && last.bottom > TOP_EDGE) return { el, text: said, nth: seen.get(said) ?? 0 };
    seen.set(said, (seen.get(said) ?? 0) + 1);
  }
  return null;
}

function fraction(): number {
  const room = document.documentElement.scrollHeight - window.innerHeight;
  return room > 0 ? window.scrollY / room : 0;
}

function anchorOf(found: { el: HTMLElement; text: string; nth: number } | null): ViewAnchor | null {
  if (!found) return null;
  const rect = found.el.getClientRects()[0] ?? found.el.getBoundingClientRect();
  return { text: found.text, nth: found.nth, top: rect.top, fraction: fraction() };
}

/** lineAtTop is the reading view's first line on screen. */
export function lineAtTop(): ViewAnchor | null {
  return anchorOf(firstBelowEdge<HTMLElement>("[data-line]", (el) => el.textContent ?? ""));
}

/** fieldAtTop is the editor's first paragraph box on screen. */
export function fieldAtTop(): ViewAnchor | null {
  return anchorOf(firstBelowEdge<HTMLTextAreaElement>("[data-field-key]", (el) => el.value));
}

function settle(el: HTMLElement | null, anchor: ViewAnchor) {
  if (el) {
    const rect = el.getClientRects()[0] ?? el.getBoundingClientRect();
    window.scrollBy({ top: rect.top - anchor.top });
    return;
  }
  const room = document.documentElement.scrollHeight - window.innerHeight;
  window.scrollTo({ top: anchor.fraction * room });
}

/**
 * showLine puts the reading view's line that opens the anchor's paragraph
 * where the paragraph box was. The reading view cuts a paragraph into
 * sentences, and a sentence can recur, so it is the line the paragraph
 * *starts* with, counted past the paragraphs above that start the same way.
 */
export function showLine(anchor: ViewAnchor) {
  const lines = Array.from(document.querySelectorAll<HTMLElement>("[data-line]"));
  const opens = lines.filter((el) => {
    const text = squash(el.textContent ?? "");
    return text !== "" && anchor.text.startsWith(text);
  });
  settle(opens[anchor.nth] ?? opens[0] ?? null, anchor);
}

/**
 * showField puts the paragraph box holding the anchor's line where the line
 * was: the box holding its nth occurrence, since the same sentence can end
 * several paragraphs.
 */
export function showField(anchor: ViewAnchor) {
  const fields = Array.from(document.querySelectorAll<HTMLTextAreaElement>("[data-field-key]"));
  let left = anchor.nth;
  let found: HTMLTextAreaElement | undefined;
  if (anchor.text !== "") {
    for (const el of fields) {
      const count = squash(el.value).split(anchor.text).length - 1;
      if (count > left) {
        found = el;
        break;
      }
      left -= count;
    }
  }
  settle(found ?? null, anchor);
}
